/**
 * Parseo y transformación de elementos visuales musicales en guías escolares.
 */

export const COLORES_PLACA = {
  rojo: { fill: '#E53935', label: 'Rojo' },
  azul: { fill: '#1E88E5', label: 'Azul' },
  verde: { fill: '#43A047', label: 'Verde' },
  amarillo: { fill: '#FDD835', label: 'Amarillo', stroke: '#CA8A04' },
  naranja: { fill: '#FB8C00', label: 'Naranja' },
  morado: { fill: '#8E24AA', label: 'Morado' },
  silencio: { fill: '#fff', label: 'Silencio', stroke: '#94A3B8', dashed: true },
  vacio: { fill: '#fff', label: 'Pinta', stroke: '#94A3B8', dashed: true }
};

const EMOJI_A_COLOR = {
  '🔴': 'rojo',
  '🔵': 'azul',
  '🟢': 'verde',
  '🟡': 'amarillo',
  '🟠': 'naranja',
  '🟣': 'morado',
  '⚪': 'silencio',
  '⬜': 'silencio'
};

const PALABRA_A_COLOR = Object.keys(COLORES_PLACA).reduce((acc, k) => {
  acc[k] = k;
  return acc;
}, {});

export const parsearTokenColor = (token = '') => {
  const t = String(token || '').trim().toLowerCase();
  if (!t) return null;
  if (EMOJI_A_COLOR[token.trim()]) return EMOJI_A_COLOR[token.trim()];
  if (PALABRA_A_COLOR[t]) return t;
  if (/silencio|pausa|shh|—|-/.test(t) && t !== 'vacio') return 'silencio';
  if (/^vacio|vac[ií]o|\?|pinta|_.+|\.+$/.test(t)) return 'vacio';
  return null;
};

export const parsearSecuenciaColores = (texto = '') => {
  const raw = String(texto || '').trim();
  if (!raw) return [];
  const porEmoji = raw.match(/[\u{1F534}\u{1F535}\u{1F7E2}\u{1F7E1}\u{1F7E0}\u{1F7E3}\u26AA\u2B1C]/gu);
  if (porEmoji?.length) return porEmoji.map((e) => EMOJI_A_COLOR[e]).filter(Boolean);
  return raw
    .split(/[,|+\/\-\s•·]+/)
    .map(parsearTokenColor)
    .filter(Boolean);
};

const esFilaSeparador = (fila = []) => fila.every((c) => /^[\s\-:|]+$/.test(String(c || '')));

export const intentarParsearDialogoMusical = (filas = []) => {
  const rows = (filas || []).filter((r) => Array.isArray(r) && r.length && !esFilaSeparador(r));
  if (rows.length < 2) return null;

  const head = rows[0].map((c) => String(c || '').toLowerCase());
  const esDialogo =
    head.some((h) => /comp[aá]s|pregunta|respuesta|turno/.test(h)) &&
    head.length >= 2;
  if (!esDialogo) return null;

  const idxPregunta = head.findIndex((h) => /pregunta|comp[aá]s\s*1|turno\s*a/i.test(h));
  const idxRespuesta = head.findIndex((h) => /respuesta|comp[aá]s\s*2|turno\s*b/i.test(h));
  const iP = idxPregunta >= 0 ? idxPregunta : 0;
  const iR = idxRespuesta >= 0 ? idxRespuesta : 1;

  const body = rows.slice(1);
  const pregunta = [];
  const respuesta = [];
  body.forEach((fila) => {
    const p = parsearSecuenciaColores(fila[iP]);
    const r = parsearSecuenciaColores(fila[iR]);
    if (p.length) pregunta.push(...p);
    if (r.length) respuesta.push(...r);
  });

  if (!pregunta.length && !respuesta.length) return null;

  const tiempos = Math.max(pregunta.length, respuesta.length, 4);
  const pad = (arr) => {
    const out = [...arr];
    while (out.length < tiempos) out.push('silencio');
    return out.slice(0, tiempos);
  };

  return {
    tipo: 'dialogo_musical',
    tiempos,
    pregunta: pad(pregunta),
    respuesta: pad(respuesta),
    etiquetaPregunta: rows[0][iP] || 'Pregunta',
    etiquetaRespuesta: rows[0][iR] || 'Respuesta'
  };
};

export const intentarParsearCompararAlturas = (filas = []) => {
  const rows = (filas || []).filter((r) => Array.isArray(r) && r.length && !esFilaSeparador(r));
  if (rows.length < 2) return null;

  const head = rows[0].map((c) => String(c || '').toLowerCase()).join(' ');
  if (!/agudo|grave|altura|sonido/.test(head)) return null;

  const pares = [];
  rows.slice(1).forEach((fila) => {
    const colores = fila.flatMap((c) => parsearSecuenciaColores(c)).filter(Boolean);
    const unicos = [...new Set(colores)].filter((c) => c !== 'silencio');
    if (unicos.length >= 2) {
      pares.push({ a: unicos[0], b: unicos[1], marcar: 'agudo' });
    } else if (unicos.length === 1 && fila[0]) {
      const nombre = String(fila[0]).trim();
      const c = parsearTokenColor(nombre) || unicos[0];
      if (c) pares.push({ a: c, b: null, marcar: 'agudo', solo: true });
    }
  });

  if (pares.length === 0) {
    const nombres = rows
      .slice(1)
      .map((f) => parsearTokenColor(f[0]))
      .filter(Boolean);
    if (nombres.length >= 2) {
      for (let i = 0; i < nombres.length - 1; i += 2) {
        pares.push({ a: nombres[i], b: nombres[i + 1], marcar: i % 4 === 0 ? 'agudo' : 'grave' });
      }
    }
  }

  if (!pares.length) return null;
  return { tipo: 'comparar_alturas', pares };
};

const parseSecuenciaDialogo = (texto = '') => {
  const t = String(texto || '').trim();
  if (!t || /^(vacio|vac[ií]os?|\?|\.+|_+|-+|pinta)$/i.test(t)) return [];
  const seq = parsearSecuenciaColores(t);
  return seq.length ? seq : [];
};

const matchDialogoLinea = (linea = '') => {
  const m = linea.match(/^\[Di[aá]logo(?:\s+musical)?[:\s]+(.+)\]$/i);
  if (!m) return null;
  const inner = m[1];
  const tiemposM = inner.match(/(\d+)\s*\/\s*4|tiempos?\s*[=:]\s*(\d+)/i);
  const tiemposPorCompas = tiemposM ? parseInt(tiemposM[1] || tiemposM[2], 10) : 4;
  const compasesM = inner.match(/(?:compases?|plantilla)\s*[=:]\s*(\d+)/i);
  const compases = compasesM ? Math.min(2, Math.max(1, parseInt(compasesM[1], 10))) : 1;
  const esPlantillaExplicita = /(?:modo\s*=\s*)?(plantilla|crear|vac[ií]o)|\bplantilla\b/i.test(inner);

  const preM = inner.match(/pregunta\s*[=:]\s*([^;]+)/i);
  const resM = inner.match(/respuesta\s*[=:]\s*([^;]+)/i);

  const preguntaRaw = preM ? parseSecuenciaDialogo(preM[1]) : null;
  const respuestaRaw = resM ? parseSecuenciaDialogo(resM[1]) : null;

  const inventar =
    esPlantillaExplicita ||
    (preM && Array.isArray(preguntaRaw) && preguntaRaw.length === 0) ||
    (resM && Array.isArray(respuestaRaw) && respuestaRaw.length === 0);

  const tCompas = Math.max(2, Math.min(4, tiemposPorCompas || 4));
  const totalSlots = tCompas * compases;

  if (inventar) {
    return {
      tipo: 'dialogo_musical',
      modo: 'plantilla',
      compases,
      tiempos: tCompas,
      pregunta: Array(totalSlots).fill('vacio'),
      respuesta: Array(totalSlots).fill('vacio'),
      etiquetaPregunta: 'Pregunta',
      etiquetaRespuesta: 'Respuesta'
    };
  }

  if (!preM && !resM) return null;

  const pad = (arr) => {
    const out = [...(arr || [])];
    while (out.length < totalSlots) out.push('silencio');
    return out.slice(0, totalSlots);
  };

  const pregunta = pad(preguntaRaw);
  const respuesta = pad(respuestaRaw);
  if (!pregunta.some((c) => c && c !== 'silencio') && !respuesta.some((c) => c && c !== 'silencio')) {
    return null;
  }

  return {
    tipo: 'dialogo_musical',
    modo: 'ejemplo',
    compases: 1,
    tiempos: tCompas,
    pregunta,
    respuesta,
    etiquetaPregunta: 'Pregunta',
    etiquetaRespuesta: 'Respuesta'
  };
};

const matchCompararLinea = (linea = '') => {
  const m = linea.match(/^\[Comparar(?:\s+alturas)?[:\s]+([^\]]+)\]$/i);
  if (!m) return null;
  const pares = [];
  m[1].split(';').forEach((chunk, idx) => {
    const t = chunk.trim();
    if (!t) return;
    const partesPipe = t.split('|').map((s) => s.trim()).filter(Boolean);
    if (partesPipe.length >= 2) {
      const a = parsearTokenColor(partesPipe[0]);
      const b = parsearTokenColor(partesPipe[1]);
      if (a && b) pares.push({ a, b, marcar: idx % 2 === 0 ? 'agudo' : 'grave' });
      return;
    }
    const vsPartes = t.split(/\s+vs\s+|\s+y\s+/i).map((s) => s.trim()).filter(Boolean);
    const cols = vsPartes.flatMap((p) => parsearSecuenciaColores(p));
    if (cols.length >= 2) {
      pares.push({ a: cols[0], b: cols[1], marcar: idx % 2 === 0 ? 'agudo' : 'grave' });
    }
  });
  if (!pares.length) return null;
  return { tipo: 'comparar_alturas', pares };
};

/** Convierte tablas con emojis/colores en marcadores reutilizables por el parser. */
export const transformarTablasEnElementosVisuales = (texto = '') => {
  const lineas = String(texto || '').split('\n');
  const out = [];
  let buf = [];

  const flushBuf = () => {
    if (!buf.length) return;
    const dialogo = intentarParsearDialogoMusical(buf);
    if (dialogo) {
      out.push(
        `[Diálogo musical: pregunta=${dialogo.pregunta.join(',')}; respuesta=${dialogo.respuesta.join(',')}; tiempos=${dialogo.tiempos}]`
      );
      buf = [];
      return;
    }
    const comparar = intentarParsearCompararAlturas(buf);
    if (comparar) {
      const specs = comparar.pares
        .filter((p) => p.a && p.b)
        .map((p) => `${p.a}|${p.b}`)
        .join('; ');
      out.push(`[Comparar alturas: ${specs}]`);
      buf = [];
      return;
    }
    out.push(...buf.map((f) => `| ${f.join(' | ')} |`));
    buf = [];
  };

  lineas.forEach((linea) => {
    const t = linea.trim();
    if (t.startsWith('|') && t.endsWith('|')) {
      if (/^\|[\s\-:|]+\|$/.test(t)) {
        if (buf.length) buf.push(t.slice(1, -1).split('|').map((c) => c.trim()));
        return;
      }
      buf.push(t.slice(1, -1).split('|').map((c) => c.trim()));
      return;
    }
    flushBuf();
    out.push(linea);
  });
  flushBuf();
  return out.join('\n');
};

export const parsearLineaElementoMusical = (linea = '') => {
  const dialogo = matchDialogoLinea(linea);
  if (dialogo) return dialogo;
  return matchCompararLinea(linea);
};

const esLineaMarcadorHuerfana = (linea = '') => {
  const t = String(linea || '').trim();
  if (!t) return false;
  if (/^\[Di[aá]logo(?:\s+musical)?[:\s]*$/i.test(t)) return true;
  if (/^\[Comparar(?:\s+alturas)?[:\s]*$/i.test(t)) return true;
  return false;
};

/** Quita viñeta "- " / "• " para detectar marcadores dentro de listas. */
export const normalizarLineaParaMarcadores = (linea = '') => {
  let t = String(linea || '')
    .trim()
    .replace(/^\*+\s*/, '')
    .replace(/\s*\*+$/, '');
  const viñeta = t.match(/^[-•]\s+(.+)$/);
  if (viñeta) return { texto: viñeta[1].trim(), eraViñeta: true };
  return { texto: t, eraViñeta: false };
};

export const parsearElementoMusicalFlexible = (linea = '') => {
  const { texto } = normalizarLineaParaMarcadores(linea);
  return parsearLineaElementoMusical(texto);
};

/** Saca marcadores visuales de viñetas a línea propia (defensa al sanitizar). */
export const promoverMarcadoresVisualesASuLinea = (texto = '') =>
  String(texto || '')
    .split('\n')
    .map((linea) => {
      const { texto: inner, eraViñeta } = normalizarLineaParaMarcadores(linea);
      if (!eraViñeta) return linea;
      if (
        /^\[(?:Di[aá]logo(?:\s+musical)?|Comparar(?:\s+alturas)?|Recuerda|Idea|Dato curioso)[:\s]/i.test(
          inner
        )
      ) {
        return inner;
      }
      return linea;
    })
    .join('\n');

/** Une marcadores partidos en varias líneas y elimina restos visibles. */
export const prepararTextoMarcadoresMusicales = (texto = '') => {
  const lineas = String(texto || '').split('\n');
  const out = [];
  let acum = null;

  const cerrarAcum = () => {
    if (!acum) return;
    const joined = acum.replace(/\s+/g, ' ').trim();
    const inner = joined
      .replace(/^\[Di[aá]logo(?:\s+musical)?[:\s]*/i, '')
      .replace(/\]\s*$/, '')
      .trim();
    if (/pregunta\s*=/i.test(inner) || /respuesta\s*=/i.test(inner)) {
      out.push(`[Diálogo musical: ${inner}]`);
    } else if (/^\[Comparar/i.test(joined)) {
      const cInner = joined.replace(/^\[Comparar(?:\s+alturas)?[:\s]*/i, '').replace(/\]\s*$/, '').trim();
      if (cInner) out.push(`[Comparar alturas: ${cInner}]`);
    }
    acum = null;
  };

  lineas.forEach((linea) => {
    const t = linea.trim();
    if (/^\]\s*$/.test(t)) {
      cerrarAcum();
      return;
    }
    if (/^\[Di[aá]logo/i.test(t) || /^\[Comparar/i.test(t)) {
      cerrarAcum();
      if (/\]\s*$/.test(t)) {
        out.push(t);
      } else {
        acum = t;
      }
      return;
    }
    if (acum) {
      acum += ` ${t}`;
      if (/\]/.test(t)) cerrarAcum();
      return;
    }
    out.push(linea);
  });
  cerrarAcum();

  return out
    .join('\n')
    .replace(/\[Di[aá]logo(?:\s+musical)?[:\s]*\]/gi, '')
    .replace(/^\s*\]\s*$/gm, '');
};
